import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Calendar, Droplet, Heart, Share2, User } from 'react-feather';
import { Hospital, XCircle, Loader, MapPin as LocationIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const AvailableRequests = ({ handleAcceptRequest }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [availableRequests, setAvailableRequests] = useState([]);
  const [acceptedRequestIds, setAcceptedRequestIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [filters, setFilters] = useState({
    bloodType: '',
    urgency: ''
  });
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [donorProfile, setDonorProfile] = useState(null);

  // Get current location when component mounts
  useEffect(() => {
    // Automatically get location on component mount
    getCurrentLocation();
    fetchAcceptedRequests();
    fetchDonorProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch available requests when filters or location changes
  useEffect(() => {
    if (currentLocation) {
      fetchAvailableRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentLocation, acceptedRequestIds, donorProfile]);

  // Fetch donor profile to get state and city
  const fetchDonorProfile = useCallback(async () => {
    try {
      const response = await api.get('/donors/me', {
        headers: {
          'x-auth-token': token
        }
      });
      
      console.log('Fetched donor profile:', response.data);
      setDonorProfile(response.data);
    } catch (error) {
      console.error('Error fetching donor profile:', error);
      // Continue with available requests even if this fails
    }
  }, [token]);

  // Fetch the donor's accepted requests
  const fetchAcceptedRequests = useCallback(async () => {
    try {
      const response = await api.get('/requests/accepted', {
        headers: {
          'x-auth-token': token
        }
      });
      
      console.log('Fetched accepted requests:', response.data);
      
      // Extract the request IDs from the accepted requests
      const acceptedIds = response.data.map(req => 
        req.requestId || req.request || req._id
      );
      
      setAcceptedRequestIds(acceptedIds);
    } catch (error) {
      console.error('Error fetching accepted requests:', error);
      // Continue with available requests even if this fails
    }
  }, [token]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    // Prevent duplicate location requests
    if (locationRequested) {
      return;
    }
    
    setLocationRequested(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation({ lat: latitude, lng: longitude });
          console.log("Location set:", { lat: latitude, lng: longitude });
          toast.success('Location updated successfully');
          // Refresh requests with the new location
          fetchAvailableRequests();
        },
        error => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your current location. Some features may be limited.');
          // Use a default location (e.g., Mumbai)
          setCurrentLocation({ lat: 19.0760, lng: 72.8777 });
          fetchAvailableRequests();
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
      // Use a default location
      setCurrentLocation({ lat: 19.0760, lng: 72.8777 });
      fetchAvailableRequests();
    }
  }, [locationRequested]);

  // Fetch available requests
  const fetchAvailableRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all blood requests from the backend
      const response = await api.get('/requests', {
        headers: {
          'x-auth-token': token
        }
      });
      
      console.log('Fetched requests:', response.data);
      
      // Apply filters on the client side
      let filteredRequests = response.data;
      
      // Filter by blood type if specified
      if (filters.bloodType) {
        filteredRequests = filteredRequests.filter(req => req.bloodType === filters.bloodType);
      }
      
      // Filter by urgency if specified
      if (filters.urgency) {
        filteredRequests = filteredRequests.filter(req => req.urgency === filters.urgency);
      }
      
      // Filter out requests that have already been accepted by this donor
      if (acceptedRequestIds.length > 0) {
        filteredRequests = filteredRequests.filter(req => 
          !acceptedRequestIds.includes(req._id)
        );
      }
      
      // Filter out requests created by the current user
      if (user && user.id) {
        console.log('Filtering out requests created by user:', user.id);
        filteredRequests = filteredRequests.filter(req => {
          // Check all possible user ID fields
          const requesterId = req.requester || req.requesterId || req.user || req.userId;
          const isOwnRequest = requesterId === user.id || 
                              (typeof requesterId === 'object' && requesterId._id === user.id);
          
          console.log(`Request ${req._id} created by ${requesterId}, isOwnRequest: ${isOwnRequest}`);
          return !isOwnRequest;
        });
      }
      
      // Filter out requests that are completed (have verified donations)
      filteredRequests = filteredRequests.filter(req => {
        // Check if any donor has verified their donation
        const hasVerifiedDonation = req.donors && req.donors.some(donor => 
          donor.status === 'donated' || donor.status === 'verified'
        );
        
        // Keep the request if it doesn't have any verified donations
        return !hasVerifiedDonation;
      });
      
      // Filter requests by user's state and city if donor profile is available
      if (donorProfile && donorProfile.address) {
        const userState = donorProfile.address.state;
        const userCity = donorProfile.address.city;
        
        if (userState && userCity) {
          console.log(`Filtering requests by user's location: ${userCity}, ${userState}`);
          filteredRequests = filteredRequests.filter(req => {
            const requestState = req.hospital?.state;
            const requestCity = req.hospital?.city;
            
            // Check if state and city exist and match
            return requestState && requestCity && 
                   requestState.toLowerCase() === userState.toLowerCase() && 
                   requestCity.toLowerCase() === userCity.toLowerCase();
          });
        }
      }
      
      // Transform the data to match the expected format
      const formattedRequests = filteredRequests
        .map(req => {
          // Format address to avoid duplication
          const hospitalAddress = req.hospital?.address || 'N/A';
          const city = req.hospital?.city || '';
          const state = req.hospital?.state || '';
          
          // Create a clean, non-duplicated address
          let formattedAddress = hospitalAddress;
          let formattedLocation = '';
          
          if (city) {
            formattedLocation += city;
          }
          
          if (state) {
            formattedLocation += formattedLocation ? `, ${state}` : state;
          }
          
          // Calculate distance if both coordinates are available
          let distance = null;
          if (currentLocation && req.location?.coordinates) {
            distance = calculateDistance(
              currentLocation.lat,
              currentLocation.lng,
              req.location.coordinates[1],
              req.location.coordinates[0]
            );
          }
          
          return {
            requestId: req._id,
            bloodGroup: req.bloodType,
            requesterName: req.patientName,
            state: state,
            city: city,
            location: formattedLocation,
            status: req.status.charAt(0).toUpperCase() + req.status.slice(1),
            hospitalName: req.hospital?.name || 'N/A',
            urgencyLevel: req.urgency === 'emergency' ? 'Critical' : 
                         req.urgency === 'urgent' ? 'High' : 'Normal',
            unitsNeeded: req.unitsNeeded,
            requestDate: formatDateWithTimeWindow(req.createdAt),
            createdAt: new Date(req.createdAt), // Add createdAt for sorting
            address: formattedAddress,
            donors: req.donors || [],
            coordinates: req.location?.coordinates ? {
              lng: req.location.coordinates[0],
              lat: req.location.coordinates[1]
            } : null,
            distance: distance
          };
        })
        .sort((a, b) => b.createdAt - a.createdAt); // Sort by date, newest first
      
      setAvailableRequests(formattedRequests);
    } catch (error) {
      console.error('Error fetching available requests:', error);
      setError('Failed to load available blood requests');
      
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        // Redirect to login page or handle as needed
        return;
      }
      
      toast.error('Failed to load available blood requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token, currentLocation, acceptedRequestIds, filters, user, donorProfile]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    
    console.log("Calculating distance between:", { lat1, lon1, lat2, lon2 });
    
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const distance = R * c; // Distance in km
    return distance.toFixed(1);
  };

  const formatDateWithTimeWindow = (dateString) => {
    const date = new Date(dateString);
    
    // Format the date
    const formattedDate = date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    // Format the time
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    
    // Calculate time window (1 hour later)
    const endDate = new Date(date);
    endDate.setHours(endDate.getHours() + 1);
    const endHours = endDate.getHours();
    const endMinutes = endDate.getMinutes();
    const formattedEndTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
    
    return `${formattedDate}, ${formattedTime} - ${formattedEndTime}`;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAcceptClick = async (request) => {
    setSelectedRequest(request);
    setShowConfirmDialog(true);
    setConfirmChecked(false);
  };

  const handleConfirmAccept = async () => {
    if (!confirmChecked) {
      toast.error('Please confirm that you want to accept this request');
      return;
    }

    try {
      setShowConfirmDialog(false);
      
      // Show loading toast
      toast.loading('Processing your request...', {
        id: 'accept-request-toast'
      });
      
      // Call the API to accept the request with proper headers and data
      await api.post(`/requests/${selectedRequest.requestId}/accept`, {
        acceptedDate: new Date().toISOString() // Add date information
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });
      
      // Add the request ID to accepted requests
      setAcceptedRequestIds(prev => [...prev, selectedRequest.requestId]);
      
      // Show enhanced success popup with Toastify
      toast.success(
        <div className="text-center">
          <div className="font-bold text-lg mb-2">Request Accepted Successfully! 🎉</div>
          <div className="mb-2">You have accepted to donate:</div>
          <div className="font-semibold text-red-600 mb-2">
            {selectedRequest.unitsNeeded} units of {selectedRequest.bloodGroup} blood
          </div>
          <div className="text-sm mb-1">
            <span className="font-medium">Hospital:</span> {selectedRequest.hospitalName}
          </div>
          <div className="text-sm mb-1">
            <span className="font-medium">Location:</span> {selectedRequest.location}
          </div>
          <div className="text-sm mb-3">
            <span className="font-medium">Patient:</span> {selectedRequest.requesterName}
          </div>
          <div className="text-sm font-medium text-gray-600">
            Redirecting to accepted requests...
          </div>
        </div>,
        {
          id: 'accept-request-toast',
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          style: {
            background: '#ffffff',
            color: '#111827',
            borderLeft: '6px solid #dc2626',
            padding: '16px',
            minWidth: '320px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }
        }
      );
      
      // Wait for 3 seconds before redirecting
      setTimeout(() => {
        navigate('/dashboard/accepted');
      }, 3000);
      
    } catch (error) {
      console.error('Error accepting request:', error);
      
      // Get the error message from the response
      const errorMessage = error.response?.data?.message || 
                          'An error occurred while accepting the request';
      
      // Check for specific error conditions
      let displayMessage = errorMessage;
      
      if (error.response?.status === 400 && errorMessage.includes('already responded')) {
        displayMessage = 'You have already accepted this blood request.';
      } else if (error.response?.status === 400 && errorMessage.includes('maximum number')) {
        displayMessage = 'This request already has the maximum number of donors and cannot accept more.';
      } else if (error.response?.status === 500) {
        displayMessage = 'Server error. Please try again later.';
      }
      
      toast.error(
        <div className="text-center">
          <div className="font-bold text-lg mb-1">Failed to Accept Request</div>
          <div>{displayMessage}</div>
        </div>,
        {
          id: 'accept-request-toast'
        }
      );
    }
  };

  return (
    <div className="relative">
      <ToastContainer />
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
          >
            <h3 className="text-xl font-bold mb-4 text-gray-900">Confirm Request Acceptance</h3>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">You are about to accept the following blood donation request:</p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="mb-2"><span className="font-medium">Blood Group:</span> {selectedRequest.bloodGroup}</p>
                <p className="mb-2"><span className="font-medium">Units Needed:</span> {selectedRequest.unitsNeeded}</p>
                <p className="mb-2"><span className="font-medium">Hospital:</span> {selectedRequest.hospitalName}</p>
                <p><span className="font-medium">Location:</span> {selectedRequest.location}</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">
                  I confirm that I want to accept this blood donation request and I will be available to donate.
                </span>
              </label>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmAccept}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  confirmChecked
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                disabled={!confirmChecked}
              >
                Accept Request
              </button>
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 bg-gray-100 text-gray-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Available Blood Requests</h2>
            <p className="text-gray-500 mt-1">
              {donorProfile && donorProfile.address && donorProfile.address.city && donorProfile.address.state ? 
                `Showing requests in ${donorProfile.address.city}, ${donorProfile.address.state}` : 
                'Find and respond to blood donation requests in your area'}
            </p>
          </div>
          
          {/* Filters and Location Button */}
          <div className="flex flex-wrap gap-3">
            <select 
              name="bloodType"
              value={filters.bloodType}
              onChange={handleFilterChange}
              className="px-4 py-2 bg-gray-50 border-none rounded-lg text-gray-600 text-sm focus:ring-2 focus:ring-red-500"
            >
              <option value="">All Blood Types</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
            </select>
            <select 
              name="urgency"
              value={filters.urgency}
              onChange={handleFilterChange}
              className="px-4 py-2 bg-gray-50 border-none rounded-lg text-gray-600 text-sm focus:ring-2 focus:ring-red-500"
            >
              <option value="">All Urgency Levels</option>
              <option value="emergency">Critical</option>
              <option value="urgent">High</option>
              <option value="normal">Normal</option>
            </select>
            <button
              onClick={getCurrentLocation}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <LocationIcon size={16} />
              Use My Location
            </button>
            <button
              onClick={fetchAvailableRequests}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Location Status */}
      {currentLocation && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <LocationIcon size={16} />
          <span>Using your current location for better results</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center">
            <Loader className="h-8 w-8 animate-spin text-red-500" />
            <p className="mt-2 text-gray-600">Loading available requests...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Requests Grid */}
          {availableRequests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableRequests.map((request, index) => (
                <motion.div
                  key={request.requestId || index}
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <div className="p-5">
                    {/* Header with Blood Group and Urgency */}
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-amber-50 p-3 rounded-lg">
                          <Droplet className="text-amber-500 w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{request.bloodGroup}</h3>
                          <p className="text-sm text-gray-500">{request.unitsNeeded} units needed</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          request.urgencyLevel === 'Critical' ? 'bg-red-50 text-red-600' :
                          request.urgencyLevel === 'High' ? 'bg-orange-50 text-orange-600' :
                          'bg-amber-50 text-amber-600'
                        }`}>
                          {request.urgencyLevel}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          {request.donors ? request.donors.length : 0}/3 donors
                        </span>
                      </div>
                    </div>
                    
                    {/* Requester Info */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2">
                        <User className="text-gray-400 w-4 h-4" />
                        <span className="text-gray-700">{request.requesterName}</span>
                      </div>
                      
                      {/* Hospital */}
                      <div className="flex items-center gap-2">
                        <Hospital className="text-gray-400 w-4 h-4" />
                        <span className="text-gray-700">{request.hospitalName}</span>
                      </div>
                      
                      {/* Location with Distance */}
                      <div className="flex items-start gap-2">
                        <MapPin className="text-gray-400 w-4 h-4 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-gray-700">{request.address}</p>
                          <p className="text-gray-500 text-sm">{request.location}</p>
                        </div>
                      </div>
                      
                      {/* Distance as separate item with icon */}
                      {request.distance && (
                        <div className="flex items-center gap-2 mt-2">
                          <LocationIcon className="text-blue-500 w-4 h-4" />
                          <span className="text-blue-600 font-medium">
                            {request.distance} km away
                          </span>
                        </div>
                      )}
                      
                      {/* Request Date */}
                      <div className="flex items-center gap-2">
                        <Calendar className="text-gray-400 w-4 h-4" />
                        <span className="text-gray-700">{request.requestDate}</span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="pt-4 flex gap-3">
                      {request.donors && request.donors.length < 3 ? (
                        <button
                          onClick={() => handleAcceptClick(request)}
                          className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors duration-200 flex items-center justify-center gap-2"
                        >
                          <Heart className="w-4 h-4" />
                          Accept Request
                        </button>
                      ) : (
                        <button
                          disabled
                          className="flex-1 bg-gray-200 text-gray-500 py-2 px-4 rounded-lg text-sm font-medium cursor-not-allowed"
                        >
                          Maximum Donors Reached (3/3)
                        </button>
                      )}
                      <button
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-xl shadow-sm p-8 text-center"
            >
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">No Requests Found</h3>
              <p className="mt-2 text-sm text-gray-500">There are currently no blood requests matching your criteria.</p>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
};

export default AvailableRequests;